import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ComprobanteDetalle, FiltroDeComprobantes, PaginaComprobantes, Resumen } from '../api';
import { listarComprobantes, obtenerComprobante, resumenDeComprobantes } from './apiDeLectura';

/**
 * El estado de servidor del listado y del detalle (KAN-146).
 *
 * **No hay mutaciones acá, y no las va a haber**: esta parte del módulo solo
 * lee (ver la nota de `apiDeLectura.ts`). Por eso tampoco hay nada que
 * invalidar — un comprobante emitido **no cambia nunca**: no se edita ni se
 * borra, se corrige con una nota de crédito, que es otro comprobante con otro
 * id. Es la razón por la que estas queries pueden tener un `staleTime` alto sin
 * riesgo de mostrar un dato viejo: el dato viejo de un comprobante es el mismo
 * dato.
 *
 * Lo que sí cambia es **qué comprobantes hay** (alguien emite uno nuevo), y eso
 * afecta al listado, no al detalle. Por eso los `staleTime` son distintos.
 */

/** Raíz de las claves. Constante para que consultar e invalidar no se separen. */
export const CLAVE_COMPROBANTES = ['comprobantes'] as const;

/** Clave de una página concreta del listado: el filtro entero forma parte de la clave. */
export function claveDeListado(filtro: FiltroDeComprobantes) {
  return [...CLAVE_COMPROBANTES, 'listado', filtro] as const;
}

/** Clave del detalle de un comprobante. */
export function claveDeDetalle(id: number) {
  return [...CLAVE_COMPROBANTES, 'detalle', id] as const;
}

/** Clave del resumen de un período. */
export function claveDeResumen(tenantId: number, desde: string, hasta: string) {
  return [...CLAVE_COMPROBANTES, 'resumen', tenantId, desde, hasta] as const;
}

/**
 * Una página del listado.
 *
 * **El filtro entero entra en la clave**, incluida la página: dos filtros
 * distintos son dos respuestas distintas y compartir caché entre ellos sería
 * mostrar los comprobantes de un rango bajo el rótulo de otro.
 *
 * `habilitada` en `false` (sin tenant elegido, o rango invertido) deja la query
 * quieta en vez de mandar un pedido que el backend va a rechazar.
 */
export function useComprobantes(
  filtro: FiltroDeComprobantes,
  habilitada: boolean,
): UseQueryResult<PaginaComprobantes, Error> {
  return useQuery({
    queryKey: claveDeListado(filtro),
    queryFn: () => listarComprobantes(filtro),
    enabled: habilitada,
    // Corto: acá sí aparecen filas nuevas cuando alguien emite.
    staleTime: 30_000,
  });
}

/**
 * El detalle de un comprobante. `staleTime` largo porque **un comprobante
 * emitido es inmutable**: sus importes, su CAE y su receptor son los que
 * viajaron a ARCA y no se corrigen editando la fila.
 */
export function useComprobante(id: number): UseQueryResult<ComprobanteDetalle, Error> {
  return useQuery({
    queryKey: claveDeDetalle(id),
    queryFn: () => obtenerComprobante(id),
    // `Number.isInteger` y no `> 0`: un id de una URL escrita a mano puede ser
    // cualquier cosa, y pedir `/comprobantes/NaN` sería un 404 seguro.
    enabled: Number.isInteger(id),
    staleTime: 5 * 60_000,
  });
}

/**
 * Los totales del período. **Es una query aparte del listado, no un agregado
 * del cliente**: el listado viene paginado, así que sumar lo que se ve daría el
 * total de la página, no el del período — y sumarlo en JavaScript rompería un
 * centavo tarde o temprano.
 *
 * Sin período completo no se pide: `desde` y `hasta` son obligatorios en el
 * contrato.
 */
export function useResumen(
  tenantId: number | null,
  desde: string,
  hasta: string,
): UseQueryResult<Resumen, Error> {
  const hayPeriodo = tenantId != null && desde !== '' && hasta !== '' && desde <= hasta;
  return useQuery({
    queryKey: claveDeResumen(tenantId ?? 0, desde, hasta),
    queryFn: () => resumenDeComprobantes(tenantId as number, desde, hasta),
    enabled: hayPeriodo,
    staleTime: 30_000,
  });
}
